import {createSlice} from '@reduxjs/toolkit';

import {getStarRatingsData} from './star-ratings-actions';
import {
    getReviewTagsData,
    setReviewTagsPeriod,
    setReviewTagsReservationTypes,
} from './review-tags-actions';
import {getReviewPromptsData} from './review-prompts-actions';
import {
    postOperatorReviewsSearchData,
    postOperatorReviewsSearchPaginatedData,
    setVerbatimReviewsSearchText,
    setVerbatimReviewsFilters,
    setVerbatimReviewsSort,
} from 'pages/facilities/driver-feedback/store/verbatim-reviews-actions';
import {FilterDrawerFiltersInitialValues} from 'pages/facilities/driver-feedback/verbatim-reviews/filters/utils';
import {type GetStarRatingsResponse} from 'api/facilities/get-star-ratings';
import {type ReviewTagsState} from 'pages/facilities/driver-feedback/review-tags/ReviewTags.types';
import {type VerbatimReviewsState} from 'pages/facilities/driver-feedback/verbatim-reviews/VerbatimReviews.types';
import {type ReviewPrompt} from 'api/facilities/get-review-prompts';

export interface DriverFeedbackFilterOptionsState {
    reviewPrompts: ReviewPrompt[];
    isLoading: boolean;
}

export interface DriverFeedbackState {
    starRatings: GetStarRatingsResponse;
    reviewTags: ReviewTagsState;
    verbatimReviews: VerbatimReviewsState;
    filterOptions: DriverFeedbackFilterOptionsState;
}

const initialState: DriverFeedbackState = {
    starRatings: {
        aggregatedRatings: [],
        cityRatings: [],
        start: null,
        end: null,
        facilityId: null,
        microclimateRatings: [],
    },
    reviewTags: {
        facilityId: 0,
        totals: {
            positive: 0,
            performancePercentage: 0,
            negative: 0,
        },
        tags: [
            {
                emoji: '',
                count: 0,
                performancePercentage: 0,
                isPositive: true,
                percentOfTotal: 0,
                label: '',
            },
        ],
        hasMicroclimateData: false,
        filters: {
            period: '30D',
            reservationTypes: [],
        },
    },
    verbatimReviews: {
        data: {
            totals: {
                positiveCount: 0,
                negativeCount: 0,
                highImpactCount: 0,
            },
            reviews: [],
            lastProcessed: null,
        },
        meta: {
            limit: 0,
            offset: 0,
            nextOffset: 0,
            totalCount: 0,
            filteredCount: 0,
        },
        filters: {
            ...FilterDrawerFiltersInitialValues,
            searchText: '',
            sortBy: 'submitted',
            limit: 25,
            offset: 0,
        },
    },
    filterOptions: {
        reviewPrompts: [],
        isLoading: false,
    },
};

const driverFeedbackSlice = createSlice({
    name: 'driverFeedback',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(setVerbatimReviewsFilters, (state, {payload}) => {
                state.verbatimReviews.filters = {
                    ...state.verbatimReviews.filters,
                    ...payload,
                };
            })
            .addCase(setVerbatimReviewsSort, (state, {payload}) => {
                state.verbatimReviews.filters.sortBy = payload;
            })
            .addCase(setVerbatimReviewsSearchText, (state, {payload}) => {
                state.verbatimReviews.filters.searchText = payload;
            })
            .addCase(
                postOperatorReviewsSearchData.fulfilled,
                (state, {payload}) => {
                    state.verbatimReviews.data = payload.data;
                    state.verbatimReviews.meta = payload.meta;
                }
            )
            .addCase(
                postOperatorReviewsSearchPaginatedData.fulfilled,
                (state, {payload}) => {
                    state.verbatimReviews.data = {
                        ...payload.data,
                        reviews: [
                            ...state.verbatimReviews.data.reviews,
                            ...payload.data.reviews,
                        ],
                    };

                    state.verbatimReviews.meta = payload.meta;
                }
            )
            .addCase(getReviewTagsData.fulfilled, (state, {payload}) => {
                state.reviewTags = {
                    ...state.reviewTags,
                    ...payload,
                    filters: state.reviewTags.filters,
                };
            })
            .addCase(setReviewTagsPeriod, (state, {payload}) => {
                state.reviewTags.filters.period = payload;
            })
            .addCase(setReviewTagsReservationTypes, (state, {payload}) => {
                state.reviewTags.filters.reservationTypes = payload;
            })
            .addCase(getStarRatingsData.fulfilled, (state, {payload}) => {
                state.starRatings = payload;
            })
            .addCase(getReviewPromptsData.pending, (state) => {
                state.filterOptions.isLoading = true;
            })
            .addCase(getReviewPromptsData.fulfilled, (state, {payload}) => {
                state.filterOptions.reviewPrompts = payload;
                state.filterOptions.isLoading = false;
            })
            .addCase(getReviewPromptsData.rejected, (state) => {
                state.filterOptions.isLoading = false;
            });
    },
});

export default driverFeedbackSlice.reducer;
